import { Button, Grid, useCheckboxGroup } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlowsQuestion } from '../../../../flows/src/types/FlowsTypes';
import useFlowInputComponent from '../../../../flows/src/hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../flows/src/hooks/useQuestionEnum';
import MultiSelectOtherInput from './MultiSelectOtherInput';
import MultiSelectInputCheckbox from './MultiSelectInputCheckbox';
import { Option } from '../../../../../types/Form';
import { uniq } from 'lodash';

interface Props {
  question: FlowsQuestion;
}

const MultiSelectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const {
    field: { value: selected, onBlur },
  } = useFlowInputComponent({ question, defaultValue: [] });
  const options = useQuestionEnum(question);

  const isAllSelected = options.every(({ value }) =>
    (selected as string[])?.includes(value),
  );

  const onButtonClick = () => {
    if (isAllSelected) {
      // De-select all options
      onBlur({ name: question.id, value: [] });
    } else {
      // Select all options
      onBlur({
        name: question.id,
        value: options.map(({ value }) => value),
      });
    }
  };

  // If there are more then 12 checkboxes it will only show 7
  const wrappedOptions = options.length > 12 ? options?.slice(0, 7) : options;
  const wrappedSelected = selected.filter((option: string) =>
    wrappedOptions.map(({ value }) => value).includes(option),
  );

  const otherOptions = options.length > 8 ? options?.slice(7) : [];
  const otherSelected = selected.filter((option: string) =>
    otherOptions.map(({ value }) => value).includes(option),
  );

  const handleOnSelectOthers = (selectedOptions: Option[]) => {
    onBlur({
      name: question.id,
      value: uniq([
        ...wrappedSelected,
        ...selectedOptions.map(({ value }) => value),
      ]),
    });
  };

  const { value: groupValues, getCheckboxProps } = useCheckboxGroup({
    value: (selected as string[]) || [],
    onChange: (value) => onBlur({ name: question.id, value: value }),
  });

  const columnsAmount =
    wrappedOptions.length < 4 ? wrappedOptions.length : 'auto-fill';

  return (
    <Grid
      w="100%"
      justifyContent="center"
      justifyItems="center"
      templateColumns={{
        base: '1fr',
        mobileS: 'repeat(2, 1fr)',
        tabletS: `repeat(${columnsAmount}, minmax(120px, 210px))`,
      }}
      gridGap={{ base: '2', tabletS: '4', tablet: '6' }}
    >
      {/* SELECT ALL BUTTON */}
      {options.length <= 12 && !question.rules.is_disabled && (
        <Button
          gridColumn="1/-1"
          variant="link"
          onClick={onButtonClick}
          ml="auto"
        >
          {isAllSelected ? t('Deselect all') : t('Select all')}
        </Button>
      )}

      {/* CHECKBOXES */}
      {wrappedOptions.map((option) => {
        const useCheckboxProps = getCheckboxProps({ value: option.value });
        return (
          <MultiSelectInputCheckbox
            key={option.value}
            option={option}
            iconKey={option.icon_key}
            isActive={groupValues.includes(option.value)}
            useCheckboxProps={useCheckboxProps}
            isDisabled={question.rules.is_disabled}
          />
        );
      })}
      {/* OTHERS BUTTON */}
      {options.length > 12 && (
        <MultiSelectOtherInput
          onClose={handleOnSelectOthers}
          options={otherOptions}
          selected={otherSelected}
          isDisabled={question.rules.is_disabled}
        />
      )}
    </Grid>
  );
};

export default MultiSelectInputComponent;
